import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatTimeAgo } from "@/zapi-frontend/utils/dates";
import type { AppNotification, AppNotificationKind, NotificationsLabels } from "../ui/blocks/NotificationsMenu";
import { useNotifications } from "./useNotifications";
import { TNotification } from "./store";
import { getNotificationContent, getNotificationTarget } from "./notificationContent";

const kindByType: Record<TNotification["type"], AppNotificationKind> = {
  chat_responded: "leo-reply",
  project_ready: "project-ready"
};

const newestFirst = (a: TNotification, b: TNotification) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

export function useNotificationsMenu() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead } = useNotifications();

  const items = useMemo<AppNotification[]>(
    () =>
      [...notifications].sort(newestFirst).map((notification) => {
        const content = getNotificationContent(t, notification);
        return {
          id: String(notification.id),
          kind: kindByType[notification.type],
          title: content.title,
          description: content.subtitle || undefined,
          time: formatTimeAgo(notification.createdAt),
          unread: !notification.isRead
        };
      }),
    [notifications, t]
  );

  const labels: NotificationsLabels = {
    title: t("notifications.title"),
    trigger: (count) => (count > 0 ? t("notifications.triggerUnread", { count }) : t("notifications.title")),
    markAllRead: t("notifications.markAllAsRead"),
    markRead: t("notifications.markRead"),
    markGroupRead: (name) => t("notifications.markGroupRead", { name }),
    groupSummary: (count, unread) =>
      unread > 0
        ? `${t("notifications.groupCount", { count })} · ${t("notifications.groupUnread", { count: unread })}`
        : t("notifications.groupCount", { count }),
    unread: t("notifications.unread"),
    loading: t("notifications.loadingList"),
    errorTitle: t("notifications.loadFailed"),
    errorDescription: t("notifications.loadFailedHint"),
    retry: t("notifications.retry"),
    emptyTitle: t("notifications.caughtUpTitle"),
    emptyDescription: t("notifications.caughtUpDescription")
  };

  const onSelect = (item: AppNotification) => {
    const notification = notifications.find((candidate) => String(candidate.id) === item.id);
    if (!notification) return;
    if (!notification.isRead) void markAsRead(notification.id);
    const target = getNotificationTarget(notification);
    if (target) navigate(target);
  };

  return { notifications: items, labels, onSelect, onMarkAllRead: () => void markAllAsRead() };
}
